import {
    MouseEvent as ReactMouseEvent,
    ReactElement,
    useCallback,
    useMemo,
    useState,
} from 'react'
import AddGroup from './AddGroup'
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    LinearProgress,
} from '@mui/material'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
    MRT_RowSelectionState,
    MRT_ColumnFiltersState,
    MRT_SortingState,
    MRT_ColumnDef,
} from 'material-react-table'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import { enqueueSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../redux/hook'
import theme from '../../theme/theme'
import { deleteGroup, fetchGroups } from '../../services/groupsServices'
import {
    IGroupformData,
    Group,
    IOpenAddOrEditGroups,
    GroupApiResponse,
} from './interfaces'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import { UserPermissions } from '../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import { defaultFormData } from './constants'
import { usePermissions } from '../../hooks/usePermissions'
import { useRoutePermission } from '../../hooks/useRoutePermission'

const fetchSize = 10
const actionColumnSize = 25

const actionCellSx = {
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    fontSize: '14px',
    padding: '0px',
    textAlign: 'center',
}

const actionIconButtonSx = {
    backgroundColor: 'transparent',
    height: 28,
    padding: '2px',
    width: 28,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                textAlign: 'center',
            },
        },
    },
}

const UserGroups = (): ReactElement => {
    /**
     * Check if user has permission to view this page, using orgs / permissions object in store. If not
     * redirect him to home page. This is to be done when user changes the org from dropdown. Show spiner
     * effect.
     */
    const navigate = useNavigate()
    const { t } = useTranslation()

    const org = useAppSelector((state) => state.org?.org)
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const { hasPermission } = usePermissions()
    const hasWriteAccess = hasPermission(UserPermissions.USER_ADMIN)
    //checking the permissions, if user doesn't have permission. redirecting to home
    useRoutePermission([
        UserPermissions.USER_ADMIN,
        UserPermissions.SYSTEM_READER,
    ])
    const onClickPreview = useCallback(
        (row: any) => {
            navigate(`/user-group/${row.name}/${row.groupId}`)
        },
        [navigate],
    )

    const [openAddOrEditGroup, setOpenAddOrEditGroup] =
        useState<IOpenAddOrEditGroups>({
            mode: 'Add',
            open: false,
            formData: defaultFormData,
        })
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )

    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [groupsToDelete, setGroupsToDelete] = useState<number[]>()
    const [isGroupsUpdated, setIsGroupsUpdated] = useState<boolean>(false)

    const handleAddOrEditGroup = (refreshTable: boolean) => {
        // Update the key value to force re-render of OrgTable component
        if (refreshTable) {
            setRowSelection({})
            setIsGroupsUpdated((prev) => !prev)
        }
        setOpenAddOrEditGroup({
            mode: 'Add',
            open: false,
            formData: defaultFormData,
        })
    }
    const handleOpen = (mode: string, data: IGroupformData) => {
        setOpenAddOrEditGroup({
            mode,
            open: true,
            formData: data,
        })
    }

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<GroupApiResponse>({
            queryKey: [
                'user-groups-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
                org,
                isGroupsUpdated,
            ],
            queryFn: async ({ pageParam }) => {
                let filterBy = sorting[0]?.id
                const sortOrder = filterBy
                    ? sorting[0]?.desc
                        ? 'DESC'
                        : 'ASC'
                    : ''
                if (filterBy === 'userCount') {
                    filterBy = 'USERS'
                }
                if (filterBy === 'Created At') {
                    filterBy = 'CREATED'
                }
                const response = await fetchGroups(
                    {
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: globalFilter?.trim() || '',
                        filterBy: filterBy ? filterBy.toUpperCase() : '',
                        sortOrder,
                    },
                    org?.id ? org.id : '',
                )
                return response as GroupApiResponse
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                // Check if we got any data
                if (!lastPage?.groups?.length) {
                    return undefined
                }

                // Calculate if we've received all data
                const totalFetched = pages.reduce(
                    (acc, page) => acc + (page?.groups?.length || 0),
                    0,
                )
                if (totalFetched >= (lastPage?.meta?.totalRowCount || 0)) {
                    return undefined
                }

                return pages.length
            },
            refetchOnWindowFocus: false,
        })

    const flatData = useMemo(
        () => data?.pages.flatMap((page) => page.groups) ?? [],
        [data],
    )

    const totalDBRowCount = data?.pages?.[0]?.meta?.totalRowCount ?? 0

    const openAddGroup = () => {
        handleOpen('Add', { name: '', description: '' })
    }
    const openEditGroup = useCallback((rowData: Group) => {
        handleOpen('Edit', {
            groupId: rowData.groupId,
            orgId: rowData.orgId,
            name: rowData.name,
            description: rowData.description,
        })
    }, [])
    const deleteGroupHandler = () => {
        const keys = Object.keys(rowSelection)
        const groupsToDelete: number[] = []
        for (const key of keys) {
            const keyNumber = Number(key)
            const rowData: Group = flatData[keyNumber]
            if (rowData.userCount > 0) {
                enqueueSnackbar(t('cannotDeleteUserGroupWithActiveUsers'), {
                    variant: 'error',
                })
                return
            } else {
                groupsToDelete.push(rowData.groupId)
            }
        }
        setGroupsToDelete(groupsToDelete)
        setOpenDeleteDialog(true)

        // Update the key value to force re-render of OrgTable component
    }

    const handleDeleteDialogOperation = async () => {
        await deleteGroup(groupsToDelete, org?.id)
        setRowSelection({})
        setIsGroupsUpdated((prev) => !prev)
        setOpenDeleteDialog(false)
    }
    const disableDeleteButton = Object.keys(rowSelection).length === 0
    const columns: MRT_ColumnDef<Group>[] = useMemo(
        () => [
            ...(hasWriteAccess
                ? [
                      {
                          header: '',
                          accessorKey: 'editid',
                          id: 'editid',
                          Cell: ({
                              row,
                          }: {
                              row: { id: string; original: Group }
                          }) => (
                              <IconButton
                                  id={`row-${row.id}-edit-button`}
                                  onClick={(event) => {
                                      event.stopPropagation()
                                      openEditGroup(row.original)
                                  }}
                                  aria-controls="simple-menu-preview"
                                  aria-haspopup="true"
                                  aria-label={
                                      hasWriteAccess
                                          ? t('editUser')
                                          : t('viewUser')
                                  }
                                  sx={actionIconButtonSx}
                                  title={
                                      hasWriteAccess
                                          ? t('editUser')
                                          : t('viewUser')
                                  }
                              >
                                  {hasWriteAccess ? (
                                      <img
                                          src="/assets/images/icons/edit.svg"
                                          alt={t('editUser')}
                                      />
                                  ) : (
                                      <img
                                          src="/assets/images/icons/preview.svg"
                                          alt={t('viewUser')}
                                      />
                                  )}
                              </IconButton>
                          ),
                          size: actionColumnSize,
                          minSize: actionColumnSize,
                          grow: false,
                          maxSize: actionColumnSize,
                          enableResizing: false,
                          enableSorting: false,
                          enableColumnFilter: false,
                          muiTableBodyCellProps: {
                              role: 'gridcell',
                              sx: actionCellSx,
                              onClick: (event: ReactMouseEvent) =>
                                  event.stopPropagation(),
                          },
                      },
                  ]
                : []),
            {
                header: '',
                accessorKey: 'previewId',
                id: 'previewId',
                Cell: ({ row }: { row: { id: string; original: Group } }) => (
                    <IconButton
                        id={`preview-group-button-${row.id}`}
                        onClick={(event) => {
                            event.stopPropagation()
                            onClickPreview(row.original)
                        }}
                        aria-controls="simple-menu-preview"
                        aria-haspopup="true"
                        aria-label={t('previewUserGroupDetails')}
                        sx={actionIconButtonSx}
                        title={t('viewUser')}
                    >
                        <img
                            src="/assets/images/icons/preview.svg"
                            alt={t('previewUserGroupDetails')}
                        />
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                    onClick: (event: ReactMouseEvent) =>
                        event.stopPropagation(),
                },
            },
            {
                accessorKey: 'name',
                header: t('name'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorKey: 'description',
                header: t('description'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorKey: 'userCount',
                header: t('totalUsers'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorFn: (row) => {
                    if (row.createdAt) {
                        return formatDateTimeWithUserPreferences(
                            row.createdAt,
                            userLocalization,
                        )
                    } else {
                        return ''
                    }
                },
                header: t('createdAt'),
                enableFilterMatchHighlighting: false,
                muiTableBodyCellProps: { role: 'gridcell' },
            },
        ],
        [userLocalization, onClickPreview, t, hasWriteAccess, openEditGroup],
    )

    if (!org) {
        return <LinearProgress />
    }

    return (
        <Grid
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Grid>
                    <Typography variant="h4" component="h2">
                        {t('userGroups')}
                    </Typography>
                </Grid>
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        role="search"
                        aria-label={t('searchUserGroups')}
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            id="user-group-list-search"
                            aria-label={t('searchUserGroups')}
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            aria-hidden="true"
                                            tabIndex={-1}
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {hasWriteAccess ? (
                        <>
                            <Box
                                sx={{
                                    cursor: disableDeleteButton
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={deleteGroupHandler}
                                    disabled={disableDeleteButton}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableDeleteButton
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableDeleteButton ? 0.5 : 1,
                                    }}
                                    id="delete-group-button"
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteUserGroup')}
                                    />
                                </Button>
                            </Box>
                            <Button
                                onClick={openAddGroup}
                                sx={{
                                    minWidth: '25px',
                                    maxWidth: '30px',
                                    marginLeft: '15px',
                                    minHeight: '25px',
                                    maxHeight: '30px',
                                    marginRight: '10px',
                                }}
                                id="add-group-button"
                            >
                                <img
                                    src={`/assets/images/icons/add-icon.svg`}
                                    alt={t('addNewUserGroup')}
                                />
                            </Button>
                        </>
                    ) : null}
                    {openAddOrEditGroup.open && (
                        <AddGroup
                            openAddOrEditGroup={openAddOrEditGroup}
                            org={org}
                            handleAddOrEditGroup={handleAddOrEditGroup}
                        />
                    )}
                </Grid>
            </Grid>
            <MRTTable<Group, GroupApiResponse>
                key={JSON.stringify(userLocalization) || 'default'}
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                maxHeight="calc(100vh - 348px)"
            />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('deleteUserGroupsConfirmation')}
            />
        </Grid>
    )
}

export default UserGroups
